import { UseQueryResult, useQuery } from "@tanstack/react-query";

import { getCarbonProgramByFarmId, getCarbonProgramByFarmIdAdmin } from "services";

import { getActiveToken, getEnvironment, useGetFarms } from "utils";

import { GET_CARBON_PROGRAM_BY_FARM_ID, GET_CARBON_PROGRAM_BY_FARM_ID_ADMIN } from "consts";

import { CarbonProgram } from "models";

export type CarbonProgramQueryResult = Pick<
  UseQueryResult<CarbonProgram>,
  "data" | "isLoading" | "isPending" | "isError" | "isSuccess" | "isFetching" | "isFetched" | "refetch" | "error" | "status"
>;

export const useGetCarbonProgramByFarmId = (farmId?: string): CarbonProgramQueryResult => {
  const isAdmin = getEnvironment().admin;
  const token = getActiveToken();

  const adminQuery = useQuery<CarbonProgram>({
    queryKey: [GET_CARBON_PROGRAM_BY_FARM_ID_ADMIN, farmId],
    queryFn: () => getCarbonProgramByFarmIdAdmin(farmId ?? ""),
    enabled: isAdmin && !!farmId && farmId !== "" && !!token,
  });

  const { currentFarm, isLoading, isPending, isError, isSuccess, isFetching, isFetched, refetch } = useGetFarms();

  const isNotCurrentFarm = !!farmId && farmId !== "" && farmId !== currentFarm?.id;

  const notCurrentFarmQuery = useQuery<CarbonProgram>({
    queryKey: [GET_CARBON_PROGRAM_BY_FARM_ID, farmId],
    queryFn: () => getCarbonProgramByFarmId(farmId ?? ""),
    enabled: !isAdmin && isNotCurrentFarm && !!token,
  });

  if (isAdmin) return adminQuery;

  if (isNotCurrentFarm) return notCurrentFarmQuery;

  return {
    data: currentFarm?.carbonProgram ?? undefined,
    isLoading,
    isPending,
    isError,
    isSuccess,
    isFetching,
    isFetched,
    refetch: refetch as unknown as CarbonProgramQueryResult["refetch"],
    error: null,
    status: isLoading ? ("pending" as const) : isError ? ("error" as const) : ("success" as const),
  };
};
